import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, ArrowRight, Sparkles, ShieldCheck, MessageCircle, Coins, DollarSign, Receipt } from "lucide-react";
import { FAQ_SECTIONS, FEE_TIERS, FUNDING_TYPES } from "@/lib/faqContent";
import usePageMeta from "@/hooks/usePageMeta";

const SECTION_ICONS = {
  "getting-started": Sparkles,
  "ndis-supports": ShieldCheck,
  "fees-funding": Coins,
  "privacy-consent": MessageCircle,
};

export default function FAQ() {
  usePageMeta({
    title: "FAQ, NDIS, Support Coordination & Recovery Coaching Questions",
    description: "Answers to common questions about NDIS Support Coordination, Recovery Coaching, referrals, fees, funding types, and how to get started with Navica Supports.",
  });
  return (
    <div data-testid="faq-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 lg:pt-20 pb-16">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E2718A] font-semibold mb-5 flex items-center justify-center gap-2">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Plain language. No spin.</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#1F2A2E] mb-7">
            Common <span className="italic" style={{ color: "var(--navica-coral)" }}>questions.</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-[#4A595E] max-w-3xl mx-auto">
            Everything we get asked most, about referrals, services, switching coordinators, and how we work.
          </p>
        </div>
      </section>

      {/* QUICK NAV */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mb-16">
        <div className="bg-white rounded-3xl border border-[#F0E5DE] p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-nav">
          {FAQ_SECTIONS.map((s) => {
            const Icon = SECTION_ICONS[s.id] || HelpCircle;
            return <QuickJump key={s.id} href={`#${s.id}`} icon={Icon} label={s.title} testId={`quicknav-${s.id}`} />;
          })}
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pb-16">
        {FAQ_SECTIONS.map((section) => (
          <div key={section.id} id={section.id} className="mb-12 scroll-mt-24" data-testid={`faq-section-${section.id}`}>
            <h2 className="font-display text-3xl lg:text-4xl text-[#1F2A2E] mb-6 flex items-center gap-3">
              <span className="w-1.5 h-8 rounded-full bg-[#E2718A]" />
              {section.title}
            </h2>
            <div className="space-y-3">
              {section.items.map((item, idx) => (
                <FAQItem key={`${section.id}-${idx}`} item={item} testId={`faq-${section.id}-${idx}`} />
              ))}
            </div>

            {/* Inline fees + funding cards under the Fees & funding Q&A */}
            {section.id === "fees-funding" && (
              <div className="mt-10 space-y-8" data-testid="fees-inline-cards">
                {/* Rate cards */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#EA823A] font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-3.5 h-3.5" /> Current NDIS rates
                  </div>
                  <div className="grid md:grid-cols-2 gap-4" data-testid="fee-tiers">
                    {FEE_TIERS.map((tier) => (
                      <article key={tier.id} className="bg-white rounded-3xl border border-[#F0E5DE] p-6" data-testid={`fee-tier-${tier.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="pr-4">
                            {tier.badge && (
                              <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-[#FCE7EB] text-[#E2718A] mb-2">
                                {tier.badge}
                              </span>
                            )}
                            <h3 className="font-display text-xl text-[#1F2A2E] leading-tight">{tier.name}</h3>
                            <code className="text-[11px] text-[#6B7A80] font-mono">{tier.code}</code>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display text-2xl text-[#E2718A]">{tier.rate}</div>
                            <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">{tier.unit}</div>
                          </div>
                        </div>
                        <p className="text-sm text-[#284952] leading-relaxed mb-3">{tier.description}</p>
                        <div className="pt-3 border-t border-[#F0E5DE]">
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">Best for</div>
                          <div className="text-xs text-[#284952]">{tier.suitsLine}</div>
                        </div>
                      </article>
                    ))}
                  </div>
                  <p className="text-xs text-[#6B7A80] mt-3">
                    Rates current at <strong>1 July 2026</strong> (NDIS Pricing Arrangements 2026-27). All NDIS supports are GST-free.
                  </p>
                </div>

                {/* Other services callout */}
                <div
                  className="rounded-3xl border p-6 flex flex-col sm:flex-row items-start gap-4"
                  style={{ background: "#FDF4E9", borderColor: "#F4D9BC" }}
                  data-testid="other-services-note"
                >
                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                       style={{ background: "#FCE7EB", color: "#E2718A" }}>
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#EA823A" }}>
                      Other services
                    </div>
                    <h3 className="font-display text-xl text-[#1F2A2E] mb-1.5 leading-tight">
                      DVA, DFV, athlete & private social work, pricing on request.
                    </h3>
                    <p className="text-sm text-[#284952] leading-relaxed">
                      Work outside the NDIS price guide (DVA wellbeing coordination, domestic and family violence support, athlete mental health, our mind and motion work, and private social work) is quoted based on scope. <Link to="/contact" className="underline font-semibold" style={{ color: "#E2718A" }}>Reach out via Connect</Link> and we&apos;ll walk you through options and fees before anything starts.
                    </p>
                  </div>
                </div>

                {/* Funding types */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#EA823A] font-semibold mb-3 flex items-center gap-2">
                    <Receipt className="w-3.5 h-3.5" /> Three ways your NDIS plan can pay us
                  </div>
                  <div className="grid md:grid-cols-3 gap-4" data-testid="funding-types">
                    {FUNDING_TYPES.map((f, idx) => (
                      <article
                        key={f.id}
                        className="bg-white rounded-3xl border border-[#F0E5DE] p-5"
                        data-testid={`funding-type-${f.id}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-[#FCE7EB] text-[#E2718A] flex items-center justify-center font-display text-base mb-3">
                          {idx + 1}
                        </div>
                        <h4 className="font-display text-base text-[#1F2A2E] mb-2 leading-tight">{f.title}</h4>
                        <p className="text-xs text-[#284952] leading-relaxed">{f.body}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-[#1A363D] text-white py-20" data-testid="faq-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl lg:text-5xl mb-5">Still have a question?</h2>
          <p className="text-white/70 text-lg mb-9 max-w-xl mx-auto">
            We answer every enquiry personally, no chatbots, no call queues. Ask anything, we'll come back to you within one business day.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="faq-cta-contact">
              Ask us a question <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/referrals" className="btn-outline-on-dark" data-testid="faq-cta-refer">
              Make a referral
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function QuickJump({ href, icon: Icon, label, testId }) {
  return (
    <a href={href} className="flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-[#F1E4D5] transition group" data-testid={testId}>
      <div className="w-9 h-9 rounded-full bg-[#FCE7EB] text-[#E2718A] flex items-center justify-center group-hover:bg-[#E2718A] group-hover:text-white transition">
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-semibold text-[#1F2A2E]">{label}</span>
    </a>
  );
}

function FAQItem({ item, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="group bg-white rounded-2xl border border-[#F0E5DE] overflow-hidden" open={open} data-testid={testId}>
      <summary
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4 hover:bg-[#F1E4D5] transition"
      >
        <span className="font-medium text-[#1F2A2E] leading-snug pr-2">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-[#E2718A] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </summary>
      <div className="px-6 pb-6 -mt-1 text-[#284952] leading-relaxed">{item.a}</div>
    </details>
  );
}
